const API_BASE = '/ai/data-sources';

function firstDefined(...vals) {
  for (let i = 0; i < vals.length; i++) {
    const v = vals[i];
    if (v !== undefined && v !== null && v !== '') return v;
  }
  return undefined;
}

function flattenParamFields(fields, out) {
  const acc = out || [];
  if (!Array.isArray(fields)) return acc;
  fields.forEach((f) => {
    if (!f) return;
    if (Array.isArray(f.fields) && f.fields.length) {
      flattenParamFields(f.fields, acc);
    }
    const control = f.control || '';
    if (!f.key || /Row|Section|Tabs|Col/.test(control)) return;
    acc.push(f);
  });
  return acc;
}

function unwrapInvokePayload(res) {
  const envelope = res && res.data;
  let data = envelope && envelope.data !== undefined ? envelope.data : envelope;
  if (
    data
    && typeof data === 'object'
    && !Array.isArray(data)
    && data.code === 0
    && data.data !== undefined
  ) {
    data = data.data;
  }
  return data;
}

function unwrapOptionList(data) {
  if (data == null) return null;
  if (Array.isArray(data)) return data;
  if (typeof data !== 'object') return [data];
  const nested = firstDefined(data.list, data.items, data.options, data.rows, data.data);
  if (Array.isArray(nested)) return nested;
  return null;
}

/**
 * 选项需有 key；显示文本用 value（或 name/label），缺省时 key=value。
 * 既无 key 也无 value 则 invalid。
 */
function normalizeOptionItem(item) {
  if (item == null) return {ok: false};
  if (typeof item !== 'object' || Array.isArray(item)) {
    const s = String(item);
    if (!s) return {ok: false};
    return {ok: true, key: s, name: s};
  }
  const key = firstDefined(item.key, item.Key);
  const value = firstDefined(
    item.value,
    item.Value,
    item.name,
    item.label,
    item.title,
    item.text,
  );
  if (key == null || key === '') {
    if (value == null || value === '') return {ok: false};
    return {ok: false};
  }
  const name = (value == null || value === '') ? String(key) : String(value);
  const groupRaw = firstDefined(item.group, item.Group);
  let group = '';
  if (groupRaw != null && groupRaw !== '') {
    group = typeof groupRaw === 'object'
      ? String(firstDefined(groupRaw.name, groupRaw.label, groupRaw.key, '') || '')
      : String(groupRaw);
  }
  return {ok: true, key, name, group};
}

function collectFieldNames(list) {
  const seen = {};
  const order = [];
  (list || []).forEach((item) => {
    if (!item || typeof item !== 'object' || Array.isArray(item)) return;
    Object.keys(item).forEach((k) => {
      if (seen[k]) return;
      seen[k] = true;
      order.push(k);
    });
  });
  return order;
}

function pickMappedField(fields, candidates) {
  const map = {};
  (fields || []).forEach((f) => {
    map[String(f).toLowerCase()] = f;
  });
  for (let i = 0; i < candidates.length; i++) {
    const hit = map[String(candidates[i]).toLowerCase()];
    if (hit) return hit;
  }
  return '';
}

function autoOptionMap(fields, existing, simple) {
  const prev = existing && typeof existing === 'object' ? existing : {};
  const keep = (k) => (k && fields.indexOf(k) >= 0 ? k : '');
  if (simple) {
    return {
      value: keep(prev.value) || pickMappedField(fields, ['value', 'id', 'name', 'key']),
    };
  }
  let group = '';
  if (prev.group === '') {
    group = '';
  } else if (keep(prev.group)) {
    group = prev.group;
  } else {
    group = pickMappedField(fields, ['group']) || '';
  }
  return {
    name: keep(prev.name) || pickMappedField(fields, ['name', 'key']),
    value: keep(prev.value) || pickMappedField(fields, ['value', 'id']),
    group,
  };
}

function stringifyGroup(groupRaw) {
  if (groupRaw == null || groupRaw === '') return '';
  if (typeof groupRaw === 'object') {
    return String(firstDefined(groupRaw.name, groupRaw.label, groupRaw.key, '') || '');
  }
  return String(groupRaw);
}

function mapItemByFields(item, map, simple) {
  if (!map) return normalizeOptionItem(item);
  if (item == null) return {ok: false};
  if (typeof item !== 'object' || Array.isArray(item)) {
    return normalizeOptionItem(item);
  }
  const group = stringifyGroup(
    map.group ? item[map.group] : firstDefined(item.group, item.Group),
  );
  if (simple || (map.value && !map.name)) {
    if (!map.value) return {ok: false};
    const v = item[map.value];
    if (v == null || v === '') return {ok: false};
    const s = String(v);
    return {ok: true, key: s, name: s, group};
  }
  const keyVal = map.value
    ? item[map.value]
    : firstDefined(item.key, item.value, item.id);
  const nameVal = map.name
    ? item[map.name]
    : firstDefined(item.name, item.label, item.title, keyVal);
  if (keyVal == null || keyVal === '') return {ok: false};
  const name = (nameVal == null || nameVal === '') ? String(keyVal) : String(nameVal);
  return {ok: true, key: keyVal, name, group};
}

function parseOptionResult(data, meta) {
  const list = unwrapOptionList(data);
  if (!list) {
    return {
      options: [],
      fields: [],
      hint: '数据源未返回选项列表。每项需包含 key，显示可用 value，缺省时 key=value',
    };
  }
  const fields = collectFieldNames(list);
  const map = meta && meta.map;
  const simple = !!(meta && meta.simple);
  const options = [];
  let missing = 0;
  list.forEach((item) => {
    const n = map ? mapItemByFields(item, map, simple) : normalizeOptionItem(item);
    if (!n.ok) missing += 1;
    else options.push({key: n.key, name: n.name, group: n.group || ''});
  });
  if (!list.length) {
    return {options: [], fields, hint: ''};
  }
  if (missing) {
    return {
      options,
      fields,
      hint: map
        ? '部分返回项缺少所选映射字段'
        : '部分返回项缺少 key/value。显示只需 key，缺省时按 key=value',
    };
  }
  return {options, fields, hint: ''};
}

function paramFieldsToProperties(paramForm) {
  const fields = flattenParamFields(paramForm && paramForm.fields);
  return fields.map((f) => ({
    key: f.key,
    label: f.name || f.key,
    required: !!f.required,
    description: (f.config && (f.config.placeholder || f.config.hint)) || '',
    default: f.config && f.config.defaultValue,
    placeholder: (f.config && f.config.placeholder) || '请输入',
  }));
}

export {
  API_BASE,
  unwrapInvokePayload,
  unwrapOptionList,
  normalizeOptionItem,
  collectFieldNames,
  pickMappedField,
  autoOptionMap,
  mapItemByFields,
  parseOptionResult,
  paramFieldsToProperties,
};
